import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowUpRight,
  Target,
  Shield,
  Zap,
  Bot,
  UserPlus,
  Link as LinkIcon
} from 'lucide-react';
import { ParallaxProvider } from 'react-scroll-parallax';
import { motion, AnimatePresence, useAnimation, useScroll, useTransform, useSpring } from 'framer-motion';
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer 
} from 'recharts';
import { useDarkMode } from '../context/DarkModeContext';
import { Canvas, useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { PointMaterial, Points, useGLTF } from '@react-three/drei';
import Footer from '../components/Footer';

// Define types for FeatureBox props
interface FeatureBoxProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  description: string;
  darkMode: boolean;
  index: number;
}

// Enhanced FeatureBox with more organic animations
const FeatureBox: React.FC<FeatureBoxProps> = ({ icon: Icon, title, description, darkMode, index }) => {
  const boxRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: boxRef,
    offset: ["start end", "end start"]
  });

  // Slightly offset animations based on index
  const y = useTransform(scrollYProgress, [0, 1], [100 + index * 20, 0]);
  const opacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);
  const rotation = useTransform(scrollYProgress, [0, 1], [index % 2 === 0 ? -10 : 10, 0]);

  return (
    <motion.div 
      ref={boxRef}
      style={{ y, opacity, scale, rotate: rotation }}
      className={`
        relative overflow-hidden
        ${darkMode ? 'bg-[#EEF4E1]/5' : 'bg-[#EEF4E1]/80'}
        backdrop-blur-lg p-8 rounded-2xl
        border border-[#68A511]/20
        group
      `}
      whileHover={{ 
        scale: 1.02,
        rotate: index % 2 === 0 ? -2 : 2,
        transition: { type: "spring", stiffness: 400, damping: 25 }
      }}
    >
      {/* Animated gradient background */}
      <motion.div
        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700"
        style={{
          background: darkMode
            ? 'radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(104,165,17,0.2), transparent 100%)'
            : 'radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(104,165,17,0.3), transparent 100%)'
        }}
      />

      {/* Icon container with enhanced animations */}
      <motion.div
        className="w-12 h-12 bg-[#68A511] rounded-xl flex items-center justify-center mb-6"
        whileHover={{ 
          scale: 1.1,
          rotate: [0, -10, 10, 0],
          transition: { duration: 0.5 }
        }}
      >
        <Icon className="w-6 h-6 text-[#EEF4E1]" />
      </motion.div>

      <motion.h3 
        className="text-xl font-bold mb-4"
        style={{ color: darkMode ? '#EEF4E1' : '#598615' }}
      >
        {title}
      </motion.h3>

      <motion.p
        className={`${darkMode ? 'text-[#DCE9C2]' : 'text-[#68A511]'}`}
        initial={{ opacity: 0.8 }}
        whileHover={{ opacity: 1 }}
      >
        {description}
      </motion.p>

      {/* Decorative elements */}
      <motion.div
        className="absolute -bottom-2 -right-2 w-20 h-20 opacity-10"
        style={{
          background: `radial-gradient(circle, ${darkMode ? '#EEF4E1' : '#68A511'} 0%, transparent 70%)`
        }}
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.1, 0.2, 0.1],
        }}
        transition={{
          duration: 3,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      />
    </motion.div>
  );
};

// Mock data for the chart
const chartData = [
  { name: 'Jan', value: 2400 },
  { name: 'Feb', value: 1398 },
  { name: 'Mar', value: 3200 },
  { name: 'Apr', value: 2780 },
  { name: 'May', value: 1890 },
  { name: 'Jun', value: 2390 },
];

// Add these keyframe animations to your global CSS or a styled-components definition
const gradientAnimation = {
  background: 'linear-gradient(180deg, #EEF4E1, #DCE9C2, #68A511)',
  backgroundSize: '300% 300%',
  animation: 'gradientMove 15s infinite ease-in-out'
};

// Sparkle component for adding magical effects
const Sparkle = ({ style }: { style?: React.CSSProperties }) => (
  <motion.div
    className="absolute w-1 h-1 bg-white rounded-full"
    style={style}
    initial={{ scale: 0, opacity: 0 }}
    animate={{
      scale: [0, 1, 0],
      opacity: [0, 1, 0],
    }}
    transition={{
      duration: 1,
      repeat: Infinity,
      repeatDelay: Math.random() * 3,
    }}
  />
);

// Refined Particle Flow System
const FlowField = () => {
  const count = 100;
  const [positions] = useState(() => {
    const pos = new Float32Array(count * 3);
    for (let i = 0; i < count; i++) {
      pos[i * 3] = (Math.random() - 0.5) * 10;
      pos[i * 3 + 1] = (Math.random() - 0.5) * 10;
      pos[i * 3 + 2] = (Math.random() - 0.5) * 10;
    }
    return pos;
  });

  useFrame((state) => {
    const time = state.clock.getElapsedTime() * 0.5;
    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      positions[i3] = Math.sin(time + i * 0.1) * 5;
      positions[i3 + 1] = Math.cos(time + i * 0.1) * 5;
      positions[i3 + 2] = Math.sin(time * 0.5 + i * 0.1) * 2;
    }
  });

  return (
    <Points positions={positions}>
      <PointMaterial
        transparent
        size={0.1}
        sizeAttenuation={true}
        depthWrite={false}
        blending={THREE.AdditiveBlending}
        vertexColors
        color="#68A511"
      />
    </Points>
  );
};

// Enhanced Typography Component
const GlowingText = ({ children, className = "" }) => {
  const [isHovered, setIsHovered] = useState(false);
  
  return (
    <motion.span
      className={`relative font-orbitron ${className}`}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <motion.span
        className="absolute inset-0 blur-lg"
        style={{
          color: "#68A511",
          opacity: isHovered ? 0.8 : 0.4
        }}
      >
        {children}
      </motion.span>
      <motion.span
        className="relative"
        animate={{
          textShadow: isHovered 
            ? "0 0 8px rgba(104, 165, 17, 0.8)"
            : "0 0 4px rgba(104, 165, 17, 0.4)"
        }}
      >
        {children}
      </motion.span>
    </motion.span>
  );
};

// Refined CTA Button
const PremiumCTA = ({ children, to, className = "" }) => {
  const buttonRef = useRef(null);
  const [isHovered, setIsHovered] = useState(false);
  
  useEffect(() => {
    const button = buttonRef.current;
    const handleMouseMove = (e) => {
      const rect = button.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;
      button.style.setProperty('--mouse-x', `${x}px`);
      button.style.setProperty('--mouse-y', `${y}px`);
    };

    button.addEventListener('mousemove', handleMouseMove);
    return () => button.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <motion.div
      ref={buttonRef}
      className="relative group"
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      <motion.div
        className="absolute -inset-1 rounded-lg bg-gradient-to-r from-[#68A511] to-[#598615] blur-lg"
        animate={{
          opacity: isHovered ? 0.7 : 0.3
        }}
      />
      <Link
        to={to}
        className={`
          relative px-10 py-5 rounded-lg
          bg-black/80 backdrop-blur-xl
          border border-[#68A511]/30
          text-white font-semibold text-xl
          flex items-center gap-3
          overflow-hidden
          ${className}
        `}
      >
        <motion.div
          className="absolute inset-0"
          style={{
            background: `radial-gradient(circle at var(--mouse-x) var(--mouse-y), rgba(104,165,17,0.4), transparent 100px)`
          }}
        />
        <span className="relative z-10">{children}</span>
        <motion.div
          animate={{
            x: isHovered ? 5 : 0
          }}
        >
          <ArrowUpRight className="w-6 h-6" />
        </motion.div>
      </Link>
    </motion.div>
  );
};

// Enhanced Hero Section
const HeroSection = ({ darkMode }) => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll();
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });

  const y = useSpring(useTransform(scrollYProgress, [0, 1], [0, -200]));
  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  useEffect(() => {
    const handleMouseMove = (e) => {
      const { clientX, clientY } = e;
      const { width, height } = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: (clientX - width / 2) / width,
        y: (clientY - height / 2) / height
      });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <motion.section
      ref={containerRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden -mt-20"
      style={{ opacity }}
    >
      {/* Refined Background */}
      <div className="absolute inset-0">
        <Canvas camera={{ position: [0, 0, 5], fov: 75 }}>
          <ambientLight intensity={0.5} />
          <pointLight position={[10, 10, 10]} />
          <FlowField />
        </Canvas>
      </div>

      {/* Subtle Gradient Overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-b from-black/40 to-transparent"
        style={{
          background: `radial-gradient(circle at ${50 + mousePosition.x * 30}% ${
            50 + mousePosition.y * 30
          }%, rgba(104,165,17,0.1) 0%, transparent 70%)`
        }}
      />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4">
        <motion.div
          className="text-center"
          style={{ y }}
        >
          <GlowingText className="text-7xl md:text-8xl lg:text-9xl font-bold tracking-wider">
            GREEN GUARD
          </GlowingText>

          <motion.p
            className="mt-8 text-xl md:text-2xl text-[#EEF4E1]/80"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            Protecting Your Financial Future with AI
          </motion.p>

          <motion.div
            className="mt-12"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
          >
            <PremiumCTA to="/dashboard">
              Get Started
            </PremiumCTA>
          </motion.div>
        </motion.div>
      </div>

      {/* Previous AI Chatbot remains the same */}
      <AIChatBot />
    </motion.section>
  );
};

// Interactive Background Component
const InteractiveBackground = () => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setMousePosition({
        x: (e.clientX / window.innerWidth) * 2 - 1,
        y: -(e.clientY / window.innerHeight) * 2 + 1,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="fixed inset-0 overflow-hidden">
      {/* Gradient Background */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            'radial-gradient(circle at 50% 50%, #68A511 0%, #598615 100%)',
            'radial-gradient(circle at 30% 70%, #68A511 0%, #598615 100%)',
            'radial-gradient(circle at 70% 30%, #68A511 0%, #598615 100%)',
          ]
        }}
        transition={{ duration: 10, repeat: Infinity, repeatType: 'reverse' }}
      />

      {/* Animated Particles */}
      <div className="absolute inset-0 opacity-30">
        {Array.from({ length: 50 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#EEF4E1]"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              x: [0, (mousePosition.x * 20)],
              y: [0, (mousePosition.y * 20)],
              opacity: [0.3, 0.7, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              repeatType: 'reverse',
              delay: Math.random() * 2,
            }}
          />
        ))}
      </div>

      {/* Animated Wave Lines */}
      <svg className="absolute inset-0 w-full h-full opacity-10">
        <motion.path
          d="M 0 50 Q 300 100 600 50 Q 900 0 1200 50"
          stroke="#DCE9C2"
          strokeWidth="2"
          fill="none"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 2, repeat: Infinity }}
        />
      </svg>
    </div>
  );
};

// Graph data
const graphData = [
  { name: 'Jan', value: 4000 },
  { name: 'Feb', value: 3000 },
  { name: 'Mar', value: 5000 },
  { name: 'Apr', value: 2780 },
  { name: 'May', value: 1890 },
  { name: 'Jun', value: 2390 },
  { name: 'Jul', value: 3490 },
];

// Graph Component
const SecurityGraph = () => {
  return (
    <div className="w-full h-[400px]">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={graphData}>
          <CartesianGrid strokeDasharray="3 3" stroke="#DCE9C2" opacity={0.2} />
          <XAxis 
            dataKey="name" 
            stroke="#EEF4E1" 
            tick={{ fill: '#EEF4E1' }}
          />
          <YAxis 
            stroke="#EEF4E1"
            tick={{ fill: '#EEF4E1' }}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: '#598615',
              border: 'none',
              borderRadius: '8px',
              color: '#EEF4E1'
            }}
          />
          <Line 
            type="monotone" 
            dataKey="value" 
            stroke="#68A511"
            strokeWidth={3}
            dot={{ fill: '#68A511', strokeWidth: 2 }}
            activeDot={{ r: 8, fill: '#DCE9C2' }}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

function Landing() {
  const { darkMode, setDarkMode } = useDarkMode();
  const [showMore, setShowMore] = useState(false);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const backgroundControls = useAnimation();
  const particleControls = useAnimation();
  const containerRef = useRef(null);
  const { scrollYProgress, scrollY } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });
  const [isScrolled, setIsScrolled] = useState(false);
  const [showFloatingNav, setShowFloatingNav] = useState(false);
  const staticNavRef = useRef(null);
  const floatingNavRef = useRef(null);
  const [scrolling, setScrolling] = useState(false);

  // Parallax values
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "50%"]);
  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [1, 0.8, 0]);

  // Track mouse movement
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      const { clientX, clientY } = e;
      const moveX = clientX - window.innerWidth / 2;
      const moveY = clientY - window.innerHeight / 2;
      setMousePosition({ x: moveX, y: moveY });
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Animate background based on mouse position
  useEffect(() => {
    backgroundControls.start({
      x: mousePosition.x / 50,
      y: mousePosition.y / 50,
      transition: {
        type: "spring",
        stiffness: 150,
        damping: 15,
        mass: 0.1
      }
    });

    particleControls.start({
      x: mousePosition.x / 25,
      y: mousePosition.y / 25,
      transition: {
        type: "spring",
        stiffness: 400,
        damping: 30,
        mass: 0.1
      }
    });
  }, [mousePosition, backgroundControls, particleControls]);

  // Generate random sparkle positions
  const sparkles = Array.from({ length: 20 }).map((_, i) => ({
    id: i,
    style: {
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
    },
  }));

  // Handle scroll position with a more precise threshold
  useEffect(() => {
    const handleScroll = () => {
      const scrollY = window.scrollY;
      console.log('ScrollY:', scrollY);

      if (staticNavRef.current) {
        const opacity = 1 - Math.min(scrollY / 100, 1);
        staticNavRef.current.style.opacity = `${opacity}`;
        staticNavRef.current.style.pointerEvents = opacity > 0.1 ? 'auto' : 'none';
        console.log('Static Nav Opacity:', opacity);
      }

      if (floatingNavRef.current) {
        const translateY = scrollY > 0 ? 0 : -100;
        floatingNavRef.current.style.transform = `translateY(${translateY}%)`;
        console.log('Floating Nav TranslateY:', translateY);
      }

      setScrolling(scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Add this before the CTA section
  const data = [
    { name: 'Jan', value: 4000 },
    { name: 'Feb', value: 3000 },
    { name: 'Mar', value: 5000 },
    { name: 'Apr', value: 2780 },
    { name: 'May', value: 1890 },
    { name: 'Jun', value: 2390 },
    { name: 'Jul', value: 3490 },
  ];

  const steps = [
    {
      number: '01',
      icon: <UserPlus size={40} className="text-white" />,
      title: 'Sign Up',
      description: 'Create your secure account',
    },
    {
      number: '02',
      icon: <LinkIcon size={40} className="text-white" />,
      title: 'Connect',
      description: 'Link your financial accounts',
    },
    {
      number: '03',
      icon: <Shield size={40} className="text-white" />,
      title: 'Protect',
      description: 'Enable AI-powered security',
    },
    {
      number: '04',
      icon: <LineChart size={40} className="text-white" />,
      title: 'Monitor',
      description: 'Track your security status',
    },
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-grow">
        <InteractiveBackground />

        {/* Hero Section */}
        <div className="relative min-h-screen flex flex-col items-center justify-center px-4">
          <motion.div
            className="text-center z-10 max-w-4xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <motion.h1
              className="text-7xl md:text-8xl lg:text-9xl font-bold mb-6 text-[#EEF4E1]"
              animate={{
                textShadow: [
                  '0 0 20px rgba(220, 233, 194, 0.3)',
                  '0 0 40px rgba(220, 233, 194, 0.3)',
                  '0 0 20px rgba(220, 233, 194, 0.3)',
                ]
              }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              GREEN GUARD
            </motion.h1>

            <motion.p
              className="text-xl md:text-2xl text-[#DCE9C2] mb-12"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
            >
              Protecting Your Financial Future with AI
            </motion.p>

            <motion.div
              whileHover={{ scale: 1.05 }}
              className="relative group"
            >
              <Link
                to="/dashboard"
                className="inline-flex items-center gap-2 px-8 py-4
                  bg-[#598615]/20 backdrop-blur-md
                  border border-[#DCE9C2]/30
                  text-[#EEF4E1] text-xl font-medium
                  rounded-full transition-all duration-300
                  hover:bg-[#68A511]/30 hover:border-[#DCE9C2]/50"
              >
                Get Started
                <ArrowUpRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <motion.div
                className="absolute -inset-2 rounded-full bg-[#DCE9C2]/20 blur-xl"
                initial={{ opacity: 0 }}
                whileHover={{ opacity: 1 }}
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Features Section */}
        <section className="relative py-20 bg-[#EEF4E1]/10 backdrop-blur-md">
          <div className="container mx-auto px-4">
            <motion.h2 
              className="text-4xl font-bold text-center text-[#EEF4E1] mb-12"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Why Choose Green Guard?
            </motion.h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Shield className="w-8 h-8" />,
                  title: "Advanced Security",
                  description: "State-of-the-art protection for your financial data"
                },
                {
                  icon: <Target className="w-8 h-8" />,
                  title: "Smart Targeting",
                  description: "AI-powered threat detection and prevention"
                },
                {
                  icon: <Zap className="w-8 h-8" />,
                  title: "Real-time Protection",
                  description: "Instant response to potential security threats"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  className="p-6 rounded-2xl bg-[#598615]/20 backdrop-blur-md
                    border border-[#DCE9C2]/20 hover:border-[#DCE9C2]/40
                    transition-all duration-300"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="text-[#EEF4E1] mb-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-[#EEF4E1] mb-2">{feature.title}</h3>
                  <p className="text-[#DCE9C2]">{feature.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="py-20 bg-gradient-to-b from-[#68A511] to-[#527f0e]">
          <div className="container mx-auto px-4">
            <h2 className="text-4xl md:text-5xl font-bold text-center text-white mb-16 font-display">
              How It Works
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.2 }}
                  className="relative"
                >
                  <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 h-full border border-white/20 hover:bg-white/20 transition-all duration-300">
                    <div className="absolute -top-4 -left-4 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center text-white font-bold">
                      {step.number}
                    </div>
                    <div className="mb-6 p-4 bg-white/10 rounded-xl inline-block">
                      {step.icon}
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-3">
                      {step.title}
                    </h3>
                    <p className="text-white/90 text-lg">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Graph Section */}
        <section className="relative py-20">
          <div className="container mx-auto px-4">
            <motion.h2 
              className="text-4xl font-bold text-center text-[#EEF4E1] mb-16"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Security Performance
            </motion.h2>

            <motion.div
              className="bg-[#598615]/10 backdrop-blur-md rounded-2xl p-6
                border border-[#DCE9C2]/20"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <SecurityGraph />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
              {[
                {
                  title: "Threats Blocked",
                  value: "10,234",
                  change: "+12%",
                  isPositive: true
                },
                {
                  title: "Security Score",
                  value: "98.5%",
                  change: "+5%",
                  isPositive: true
                },
                {
                  title: "Response Time",
                  value: "0.3s",
                  change: "-15%",
                  isPositive: true
                }
              ].map((stat, index) => (
                <motion.div
                  key={index}
                  className="p-6 rounded-xl bg-[#598615]/20 backdrop-blur-md
                    border border-[#DCE9C2]/20"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <h3 className="text-[#DCE9C2] text-lg mb-2">{stat.title}</h3>
                  <div className="flex items-end gap-3">
                    <span className="text-3xl font-bold text-[#EEF4E1]">{stat.value}</span>
                    <span className={`text-sm ${stat.isPositive ? 'text-[#68A511]' : 'text-red-500'}`}>
                      {stat.change}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative py-20 bg-[#598615]/20 backdrop-blur-md">
          <div className="container mx-auto px-4 text-center">
            <motion.h2
              className="text-4xl font-bold text-[#EEF4E1] mb-6"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              Ready to Secure Your Financial Future?
            </motion.h2>
            <motion.p
              className="text-xl text-[#DCE9C2] mb-12 max-w-2xl mx-auto"
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
            >
              Join thousands of users who trust Green Guard for their financial security
            </motion.p>
            <motion.div
              whileHover={{ scale: 1.05 }}
              className="inline-block"
            >
              <Link
                to="/signup"
                className="inline-flex items-center gap-2 px-8 py-4
                  bg-[#68A511] text-[#EEF4E1] rounded-full
                  hover:bg-[#598615] transition-all duration-300"
              >
                Get Started Now
                <ArrowUpRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </section>

        {/* AI Chat Bot */}
        <motion.div
          className="fixed bottom-8 right-8 z-50"
          whileHover={{ scale: 1.1 }}
        >
          <motion.button
            className="p-4 rounded-full bg-[#598615]/80 backdrop-blur-md
              hover:bg-[#68A511]/80 transition-colors duration-300
              shadow-lg hover:shadow-[#68A511]/50"
            whileTap={{ scale: 0.95 }}
          >
            <Bot className="w-6 h-6 text-[#EEF4E1]" />
          </motion.button>
        </motion.div>
      </main>

      {/* Add Footer */}
      <Footer />
    </div>
  );
}

export default Landing;